/**
 * Event Registration Form Component
 *
 * A multi-step registration form for the Re:Nav-96 reunion event.
 * Handles form submission, validation, and verification code entry.
 */

import { useState, type FormEvent } from 'react';
import type {
  RegistrationFormData,
  RegistrationFormErrors,
  VerificationMethod,
  EventRegistration,
} from '@/types';
import { registrationApi, ApiClientError } from '@/services/api';

/**
 * Registration flow step
 */
type RegistrationStep = 'form' | 'verification' | 'success';

/**
 * Props for the RegistrationForm component
 */
interface RegistrationFormProps {
  /** Optional callback when registration completes successfully */
  onSuccess?: (registration: EventRegistration) => void;
}

/**
 * Email validation regex
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Phone validation regex (E.164 format or basic format)
 */
const PHONE_REGEX = /^(\+?\d{10,15}|\d{10})$/;

/**
 * Validate registration form fields
 */
function validateForm(data: RegistrationFormData): RegistrationFormErrors {
  const errors: RegistrationFormErrors = {};

  if (!data.fullName.trim()) {
    errors.fullName = 'Full name is required';
  } else if (data.fullName.trim().length < 2) {
    errors.fullName = 'Full name must be at least 2 characters';
  }

  if (!data.email.trim()) {
    errors.email = 'Email address is required';
  } else if (!EMAIL_REGEX.test(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (data.verificationMethod !== 'email') {
    if (!data.phone?.trim()) {
      errors.phone = 'Phone number is required for SMS/WhatsApp verification';
    } else if (!PHONE_REGEX.test(data.phone.replace(/[\s-]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
  }

  return errors;
}

/**
 * Event Registration Form Component
 *
 * Provides a multi-step registration flow:
 * 1. Form entry with validation
 * 2. Verification code submission
 * 3. Success state with ticket display
 *
 * @example
 * ```tsx
 * <RegistrationForm onSuccess={(reg) => console.log('Registered:', reg)} />
 * ```
 */
export function RegistrationForm({ onSuccess }: RegistrationFormProps) {
  // Step management
  const [step, setStep] = useState<RegistrationStep>('form');

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>('email');

  // Verification state
  const [ticketNumber, setTicketNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSentTo, setVerificationSentTo] = useState('');

  // Result state
  const [registration, setRegistration] = useState<EventRegistration | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<RegistrationFormErrors>({});

  /**
   * Handle form submission (step 1)
   */
  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    // Validate form
    const formData: RegistrationFormData = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      verificationMethod,
    };

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await registrationApi.create(formData);
      setTicketNumber(response.registration.ticketNumber);
      setVerificationSentTo(response.verificationSentTo);
      setStep('verification');
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (error.field) {
          setErrors({ [error.field]: error.message } as RegistrationFormErrors);
        } else {
          setErrors({ general: error.message });
        }
      } else {
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle verification code submission (step 2)
   */
  const handleVerificationSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    if (!verificationCode.trim()) {
      setErrors({ general: 'Please enter the verification code' });
      return;
    }

    setIsLoading(true);

    try {
      const response = await registrationApi.verify({
        ticketNumber,
        code: verificationCode.trim(),
        method: verificationMethod,
      });

      if (response.success && response.registration) {
        setRegistration(response.registration);
        setStep('success');
        onSuccess?.(response.registration);
      } else {
        setErrors({ general: response.message || 'Verification failed. Please try again.' });
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrors({ general: error.message });
      } else {
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle resend verification code
   */
  const handleResendCode = async () => {
    setErrors({});
    setIsLoading(true);

    try {
      await registrationApi.resendVerification({
        ticketNumber,
        method: verificationMethod,
      });
      setErrors({ general: 'Verification code resent successfully!' });
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrors({ general: error.message });
      } else {
        setErrors({ general: 'Failed to resend verification code. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Render the registration form (step 1)
   */
  const renderForm = () => (
    <form onSubmit={handleFormSubmit} className="space-y-5">
      {/* Error Message */}
      {errors.general && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400 text-sm">{errors.general}</p>
        </div>
      )}

      {/* Full Name Field */}
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-white/80 mb-2">
          Full Name
        </label>
        <input
          type="text"
          id="fullName"
          name="fullName"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          className={`input ${errors.fullName ? 'border-red-500/50' : ''}`}
          placeholder="Enter your full name"
          required
          autoComplete="name"
          disabled={isLoading}
        />
        {errors.fullName && (
          <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`input ${errors.email ? 'border-red-500/50' : ''}`}
          placeholder="your.email@example.com"
          required
          autoComplete="email"
          disabled={isLoading}
        />
        {errors.email && (
          <p className="text-red-400 text-xs mt-1">{errors.email}</p>
        )}
      </div>

      {/* Phone Field */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
          Phone Number
          <span className="text-white/40 font-normal ml-1">
            {verificationMethod === 'email' ? '(optional)' : '(required)'}
          </span>
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={`input ${errors.phone ? 'border-red-500/50' : ''}`}
          placeholder="+91 98765 43210"
          autoComplete="tel"
          disabled={isLoading}
        />
        {errors.phone && (
          <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
        )}
      </div>

      {/* Verification Method */}
      <div>
        <label className="block text-sm font-medium text-white/80 mb-3">
          Verification Method
        </label>
        <div className="grid grid-cols-3 gap-3">
          <button
            type="button"
            onClick={() => setVerificationMethod('email')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'email'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">Email</span>
          </button>
          <button
            type="button"
            onClick={() => setVerificationMethod('phone')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'phone'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span className="text-xs">SMS</span>
          </button>
          <button
            type="button"
            onClick={() => setVerificationMethod('whatsapp')}
            disabled={isLoading}
            className={`p-3 rounded-lg border transition-all text-center ${
              verificationMethod === 'whatsapp'
                ? 'bg-primary/20 border-primary text-white'
                : 'bg-secondary-light border-white/10 text-white/60 hover:border-white/30'
            }`}
          >
            <svg className="w-6 h-6 mx-auto mb-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span className="text-xs">WhatsApp</span>
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary w-full mt-6"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Registering...
          </span>
        ) : (
          'Register for the Reunion'
        )}
      </button>
    </form>
  );

  /**
   * Render the verification form (step 2)
   */
  const renderVerification = () => (
    <form onSubmit={handleVerificationSubmit} className="space-y-5">
      {/* Status Message */}
      <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg text-center">
        <svg className="w-12 h-12 mx-auto mb-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p className="text-white/80 text-sm">
          We've sent a verification code to
        </p>
        <p className="text-white font-medium mt-1">{verificationSentTo}</p>
      </div>

      {/* Error/Success Message */}
      {errors.general && (
        <div className={`p-4 rounded-lg ${
          errors.general.includes('successfully')
            ? 'bg-green-500/10 border border-green-500/20'
            : 'bg-red-500/10 border border-red-500/20'
        }`}>
          <p className={`text-sm ${
            errors.general.includes('successfully') ? 'text-green-400' : 'text-red-400'
          }`}>{errors.general}</p>
        </div>
      )}

      {/* Verification Code Field */}
      <div>
        <label htmlFor="verificationCode" className="block text-sm font-medium text-white/80 mb-2">
          Verification Code
        </label>
        <input
          type="text"
          id="verificationCode"
          name="verificationCode"
          value={verificationCode}
          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="input text-center text-2xl tracking-widest"
          placeholder="000000"
          maxLength={6}
          autoComplete="one-time-code"
          disabled={isLoading}
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || verificationCode.length < 6}
        className="btn-primary w-full"
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Verifying...
          </span>
        ) : (
          'Verify & Get Ticket'
        )}
      </button>

      {/* Resend Code Link */}
      <div className="text-center">
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isLoading}
          className="text-sm text-primary hover:text-primary-light transition-colors disabled:opacity-50"
        >
          Didn't receive the code? Resend
        </button>
      </div>
    </form>
  );

  /**
   * Render the success state (step 3)
   */
  const renderSuccess = () => (
    <div className="text-center space-y-6">
      {/* Success Icon */}
      <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
        <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      {/* Success Message */}
      <div>
        <h3 className="text-2xl font-display font-bold text-white mb-2">
          You're Registered!
        </h3>
        <p className="text-white/60">
          Welcome to Re:Nav-96. We can't wait to see you at the reunion!
        </p>
      </div>

      {/* Ticket Preview Card */}
      {registration && (
        <div
          className="relative rounded-xl overflow-hidden p-6"
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '1px solid rgba(233, 69, 96, 0.3)',
          }}
        >
          {/* Ticket Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
            <div>
              <span className="text-xs text-primary uppercase tracking-wider">Event Ticket</span>
              <h4 className="text-lg font-display font-bold text-white">Re:Nav-96</h4>
            </div>
            <div className="text-right">
              <span className="text-xs text-white/40">Ticket #</span>
              <p className="text-sm font-mono text-primary">{registration.ticketNumber}</p>
            </div>
          </div>

          {/* Ticket Details */}
          <div className="space-y-3 text-left">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="text-white">{registration.fullName}</span>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="text-white/70 text-sm">{registration.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-white">June 13th, 2026</span>
            </div>
          </div>

          {/* Decorative Dots */}
          <div className="absolute left-0 top-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-secondary" />
          <div className="absolute right-0 top-1/2 translate-x-1/2 w-4 h-4 rounded-full bg-secondary" />
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3">
        <p className="text-sm text-white/40">
          A copy of your ticket has been sent to your email.
        </p>
        <button
          onClick={() => {
            setStep('form');
            setFullName('');
            setEmail('');
            setPhone('');
            setVerificationCode('');
            setTicketNumber('');
            setRegistration(null);
            setErrors({});
          }}
          className="btn-secondary w-full"
        >
          Register Another Person
        </button>
      </div>
    </div>
  );

  return (
    <div className="card">
      {/* Step Indicator */}
      {step !== 'success' && (
        <div className="flex items-center justify-center mb-6 gap-2">
          <div className={`w-3 h-3 rounded-full ${step === 'form' ? 'bg-primary' : 'bg-white/20'}`} />
          <div className="w-8 h-0.5 bg-white/20" />
          <div className={`w-3 h-3 rounded-full ${step === 'verification' ? 'bg-primary' : 'bg-white/20'}`} />
        </div>
      )}

      {/* Render current step */}
      {step === 'form' && renderForm()}
      {step === 'verification' && renderVerification()}
      {step === 'success' && renderSuccess()}
    </div>
  );
}

export default RegistrationForm;
